import { useTranslation } from "react-i18next";import React from 'react';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import {
  AlertTriangle,
  ThermometerSun,
  ThermometerSnowflake,
  Clock,
  MapPin,
  User,
  CheckCircle2,
  XCircle,
  Pause,
  Navigation } from
'lucide-react';

export const ExcursionAlert = ({ excursion, onAcknowledge, onResolve, onReroute }) => {const { t } = useTranslation("ezparcel");
  const severityColors = {
    'Critical': 'border-red-500 bg-red-50 dark:bg-red-950/30',
    'Warning': 'border-amber-500 bg-amber-50 dark:bg-amber-950/30'
  };

  const severityBadge = {
    'Critical': 'bg-red-500 text-white animate-pulse',
    'Warning': 'bg-amber-500 text-white'
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const isHighTemp = excursion.type.toLowerCase().includes('high');

  return (
    <Card className={`border-2 ${severityColors[excursion.severity]} transition-all`}>
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${
            excursion.severity === 'Critical' ? 'bg-red-100' : 'bg-amber-100'}`
            }>
              {isHighTemp ?
              <ThermometerSun className={`h-6 w-6 ${
              excursion.severity === 'Critical' ? 'text-red-600' : 'text-amber-600'}`
              } /> :

              <ThermometerSnowflake className={`h-6 w-6 ${
              excursion.severity === 'Critical' ? 'text-red-600' : 'text-amber-600'}`
              } />
              }
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold">{excursion.type}</h3>
                <Badge className={severityBadge[excursion.severity]}>
                  {excursion.severity}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                {excursion.sensorName} • {excursion.sensorId}
              </p>
            </div>
          </div>
          <Badge variant="outline" className={
          excursion.status === 'Active' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
          }>
            {excursion.status}
          </Badge>
        </div>

        {/* Temperature Info */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center p-3 rounded-lg bg-card border">
            <p className="text-xs text-muted-foreground">{t("current", "Current")}</p>
            <p className={`text-2xl font-bold font-mono ${
            excursion.currentTemp > excursion.threshold.max ||
            excursion.currentTemp < excursion.threshold.min ?
            'text-red-600' : 'text-emerald-600'}`
            }>
              {excursion.currentTemp.toFixed(1)}{t("c", "\xB0C")}
            </p>
          </div>
          <div className="text-center p-3 rounded-lg bg-card border">
            <p className="text-xs text-muted-foreground">{t("range", "Range")}</p>
            <p className="text-lg font-medium">
              {excursion.threshold.min}{t("key_4241", "\xB0 -")}{excursion.threshold.max}{t("c", "\xB0C")}
            </p>
          </div>
          <div className="text-center p-3 rounded-lg bg-card border">
            <p className="text-xs text-muted-foreground">{t("duration", "Duration")}</p>
            <p className="text-lg font-medium flex items-center justify-center gap-1">
              <Clock className="h-4 w-4" />
              {formatDuration(excursion.duration)}
            </p>
          </div>
        </div>

        {/* Details */}
        <div className="space-y-2 mb-4 text-sm">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <span>{excursion.location}</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">{excursion.shipmentId}</Badge>
            <span className="text-muted-foreground">{excursion.shipmentName}</span>
          </div>
          <div className="p-2 rounded bg-muted/50">
            <p className="text-xs text-muted-foreground">{t("productAtRisk", "Product at risk:")}</p>
            <p className="font-medium">{excursion.product}</p>
          </div>
        </div>

        {/* Acknowledged Info */}
        {excursion.acknowledgedBy &&
        <div className="mb-4 p-2 rounded bg-blue-50 dark:bg-blue-950/30 border border-blue-200">
            <div className="flex items-center gap-2 text-sm text-blue-700 dark:text-blue-300">
              <User className="h-4 w-4" />
              <span>{t("acknowledgedBy", "Acknowledged by")}{excursion.acknowledgedBy}</span>
              <span className="text-xs text-muted-foreground">{t("at", "at")}
              {new Date(excursion.acknowledgedAt).toLocaleTimeString()}
              </span>
            </div>
          </div>
        }

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          {excursion.status === 'Active' && !excursion.acknowledgedBy &&
          <Button
            size="sm"
            className="gap-1 bg-blue-600 hover:bg-blue-700"
            onClick={() => onAcknowledge?.(excursion)}>
            
              <CheckCircle2 className="h-4 w-4" />{t("acknowledge", "Acknowledge")}

          </Button>
          }
          <Button
            variant="outline"
            size="sm"
            className="gap-1"
            onClick={() => onResolve?.(excursion)}>
            
            <XCircle className="h-4 w-4" />{t("resolve", "Resolve")}

          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-1 text-amber-600 border-amber-300">
            
            <Pause className="h-4 w-4" />{t("holdShipment", "Hold Shipment")}

          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-1 text-violet-600 border-violet-300"
            onClick={() => onReroute?.(excursion)}>
            
            <Navigation className="h-4 w-4" />{t("reroute", "Reroute")}

          </Button>
        </div>
      </CardContent>
    </Card>);

};